// scene.js — builds the Retro Bakery interior. Bigger 20×14×4m floor with four
// themed display zones (Tincture / Gummy Garden / Chocolate Cove / Drink Bar),
// Willy-Wonka-flavored hero props (chocolate fountain + gummy mountain), and
// procedurally generated room textures. No external assets.
import * as THREE from 'three';

// Room dimensions (meters). Exported so controls + products can share.
export const ROOM = Object.freeze({ W: 20, D: 14, H: 4 });

// Per-frame animated objects collected at build time. Each entry has its own
// mesh + parameters; updateAnimations(time) walks the array and sets
// transforms. Used for lava-lamp blobs (and easy to extend later).
const _animatedBlobs = [];

// Called once per frame from main.js with elapsed time in seconds.
export function updateAnimations(time) {
  for (const b of _animatedBlobs) {
    // Smooth bob along Y. sin(t * speed + phase) gives the height; an
    // additional 0.5 * sin(2t + phase) modulation makes the motion feel
    // organic instead of perfectly periodic.
    const y = b.baseY
            + Math.sin(time * b.speed + b.phase) * b.amplitude
            + Math.sin(time * b.speed * 1.7 + b.phase * 0.5) * b.amplitude * 0.25;
    b.mesh.position.y = y;
    // Subtle scale pulse so blobs feel like wax merging/splitting
    const s = b.baseScale * (1 + 0.08 * Math.sin(time * b.speed * 0.9 + b.phase));
    b.mesh.scale.set(s, s * (1.1 + 0.1 * Math.sin(time * b.speed + b.phase)), s);
  }
}

// Per-category zone configs. `axis` = the axis the case extends along.
// `faceDir` = which way the products face. Used by products.js to place items.
export const ZONES = Object.freeze({
  Tincture:  { center: [-6.0, 1.0, -6.5], axis: 'x', length: 4.0, shelves: 1, faceDir: 'front',  trim: 0xeeeeee, accent: 0xffffff },
  Gummy:     { center: [-9.5, 1.0,  0.0], axis: 'z', length: 12,  shelves: 3, faceDir: 'right',  trim: 0xff5fa3, accent: 0xff4fa3 },
  Chocolate: { center: [ 9.5, 1.0,  3.5], axis: 'z', length: 6,   shelves: 3, faceDir: 'left',   trim: 0x6a3818, accent: 0xffac3e },
  Drink:     { center: [ 9.5, 1.0, -3.5], axis: 'z', length: 6,   shelves: 3, faceDir: 'left',   trim: 0x3fe0d4, accent: 0x3fe0d4 },
});

// ---------- Public entry ----------
export function buildScene(scene) {
  buildLighting(scene);
  buildShell(scene);
  buildTrim(scene);                // baseboards + crown molding
  buildEntryArea(scene);           // door frame, welcome mat, 21+ decal, planters
  buildZoneCases(scene);
  buildCounter(scene);
  buildNeonSign(scene);
  buildBeadedCurtain(scene);
  buildPosters(scene);
  buildVinylShelf(scene);
  buildLavaLamps(scene);
  buildHangingPlants(scene);
  buildChocolateFountain(scene);   // Willy-Wonka prop #1
  buildGummyMountain(scene);       // Willy-Wonka prop #2
  buildZoneSigns(scene);           // glowing zone-name signs
}

// ---------- Lighting ----------
// Mobile GPUs frequently cap the per-fragment count of `PointLight`s in a
// shader (often 4–8), silently dropping the rest. With 12 grid lights that
// produced a "single ring of light around the player + dark distance"
// effect on phones. We now do four things to keep the room uniformly lit
// on every device:
//   1. Position-less lights (Ambient + Hemisphere) carry the base
//      illumination — they don't count against the per-fragment PointLight
//      cap, so they're guaranteed to show up on any GPU.
//   2. Only 4 corner PointLights (down from 12) — well within any mobile
//      cap. Each has a bigger reach + slower decay so coverage overlaps in
//      the middle of the room.
//   3. The 12 ceiling FIXTURES (visible disco-puck meshes) are kept so the
//      ceiling doesn't look empty — they're just decorative now, no
//      attached PointLight on most of them.
//   4. main.js attaches a small "lantern" PointLight to the camera on touch
//      devices so wherever the player looks there's at least one
//      definitely-active light.
function buildLighting(scene) {
  // Bright neutral fill — primary mobile-safe base illumination.
  scene.add(new THREE.AmbientLight(0xffffff, 1.5));
  // Sky/ground gradient. Warm tungsten-y top, cool purple floor bounce —
  // matches the disco/retro vibe and gives objects subtle vertical shading.
  scene.add(new THREE.HemisphereLight(0xfff4e0, 0x3a1a4a, 1.2));

  // Visible ceiling fixtures (decorative). 4×3 grid like before.
  const xs = [-7.5, -2.5, 2.5, 7.5];
  const zs = [-5.0, 0.0, 5.0];
  for (const x of xs) {
    for (const z of zs) {
      const fixture = new THREE.Mesh(
        new THREE.CircleGeometry(0.20, 20),
        new THREE.MeshBasicMaterial({ color: 0xfff4e0, toneMapped: false }),
      );
      fixture.rotation.x = Math.PI / 2;
      fixture.position.set(x, ROOM.H - 0.02, z);
      scene.add(fixture);
    }
  }

  // Only 4 PointLights actually emit — corner-ish positions for even-ish
  // coverage. Distance 22 + decay 1.0 makes each light reach across most
  // of the room with gentle falloff so they overlap in the middle.
  const lampPositions = [
    [-7.5, -5.0],
    [ 7.5, -5.0],
    [-7.5,  5.0],
    [ 7.5,  5.0],
  ];
  for (const [x, z] of lampPositions) {
    const lamp = new THREE.PointLight(0xfff4e0, 1.6, 22, 1.0);
    lamp.position.set(x, ROOM.H - 0.2, z);
    scene.add(lamp);
  }
}

// ---------- Room shell ----------
function buildShell(scene) {
  // Floor / ceiling / walls — the BIG surfaces of the room shell — use
  // MeshBasicMaterial so they're guaranteed to render at full brightness
  // regardless of lighting. The previous Lambert + per-vertex lighting
  // produced a "darkness past a certain range" artifact on Mali-G715 even
  // with bright ambient, because mediump precision + per-vertex Lambert on
  // a huge plane underestimates lighting away from a light's footprint.
  // Basic material sidesteps the entire lighting calculation: the texture
  // is the color you see, end of story. Decorations (fountain, gummy
  // mountain, person, displays) still use Lambert for some shading
  // variation, but the room shell can't go dark.
  // The shell materials use `polygonOffset` to push their depth values
  // BACK by a few units in the depth buffer. Anything attached to a wall
  // (posters, neon signs, "Checkout" labels on the counter, accent strips)
  // therefore always wins the depth test against the wall behind it. On
  // mobile with mediump precision this is critical — without it, coplanar
  // foreground/background surfaces flicker and tear as the camera moves
  // because the GPU can't decide which is in front. Offset values picked
  // empirically: factor 1 + units 4 is enough to dominate any reasonable
  // mediump precision wobble without visible artifacts.
  // Aggressive offset values: Mali-G715 + mediump precision needs much more
  // headroom than desktop's high-precision depth buffer. factor:4 + units:32
  // applies a 4× slope-based + 32× minimum-resolvable nudge to wall depth,
  // which on a 16-bit-effective mobile depth buffer is roughly 12cm of
  // depth-buffer space — way more than needed but safe against any wall-
  // attached item.
  const shellOffset = { polygonOffset: true, polygonOffsetFactor: 4, polygonOffsetUnits: 32 };

  const carpet = makeShagCarpetTexture();
  carpet.repeat.set(10, 7);
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(ROOM.W, ROOM.D),
    new THREE.MeshBasicMaterial({ map: carpet, ...shellOffset }),
  );
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);

  const ceil = makeCeilingTexture();
  ceil.repeat.set(8, 5);
  const ceiling = new THREE.Mesh(
    new THREE.PlaneGeometry(ROOM.W, ROOM.D),
    new THREE.MeshBasicMaterial({ map: ceil, ...shellOffset }),
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = ROOM.H;
  scene.add(ceiling);

  const buildWall = (w, h, repX) => {
    const tex = makeWoodPanelTexture();
    tex.repeat.set(repX, 1.3);
    return new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ map: tex, ...shellOffset }),
    );
  };

  const back = buildWall(ROOM.W, ROOM.H, 8);
  back.position.set(0, ROOM.H / 2, -ROOM.D / 2);
  scene.add(back);

  const front = buildWall(ROOM.W, ROOM.H, 8);
  front.rotation.y = Math.PI;
  front.position.set(0, ROOM.H / 2, ROOM.D / 2);
  scene.add(front);

  const left = buildWall(ROOM.D, ROOM.H, 6);
  left.rotation.y = Math.PI / 2;
  left.position.set(-ROOM.W / 2, ROOM.H / 2, 0);
  scene.add(left);

  const right = buildWall(ROOM.D, ROOM.H, 6);
  right.rotation.y = -Math.PI / 2;
  right.position.set(ROOM.W / 2, ROOM.H / 2, 0);
  scene.add(right);

  // "Outside" backdrop visible through the glass door — twilight purple-blue.
  // Sized to match the wider double-door in buildEntryArea (3.6 × 2.6).
  const door = new THREE.Mesh(
    new THREE.PlaneGeometry(3.6, 2.6),
    new THREE.MeshLambertMaterial({ color: 0x141022 }),
  );
  door.rotation.y = Math.PI;
  door.position.set(0, 1.30, ROOM.D / 2 - 0.01);
  scene.add(door);
}

// ---------- Wall trim: baseboards + crown molding ----------
// Adds finished-room detail. Baseboards cover the floor-wall seam where
// shadow-tinting is most needed; crown molding draws the eye to the ceiling
// and gives proportions that match real retail interiors.
function buildTrim(scene) {
  const baseMat = new THREE.MeshLambertMaterial({ color: 0x1a0d05 });
  const crownMat = new THREE.MeshLambertMaterial({ color: 0xc89a3b });
  // Baseboard 12cm tall, sticks 3cm out from each wall.
  const bbH = 0.12, bbT = 0.03;
  const crH = 0.10;

  const halfW = ROOM.W / 2, halfD = ROOM.D / 2;

  function strip(width, height, depth, x, y, z) {
    return new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), baseMat).position.set(x, y, z);
  }

  // Baseboards — one strip per wall (back, front, left, right)
  const back  = new THREE.Mesh(new THREE.BoxGeometry(ROOM.W, bbH, bbT), baseMat);
  back.position.set(0, bbH / 2, -halfD + bbT / 2);
  scene.add(back);
  const front = new THREE.Mesh(new THREE.BoxGeometry(ROOM.W, bbH, bbT), baseMat);
  front.position.set(0, bbH / 2, halfD - bbT / 2);
  scene.add(front);
  const left  = new THREE.Mesh(new THREE.BoxGeometry(bbT, bbH, ROOM.D), baseMat);
  left.position.set(-halfW + bbT / 2, bbH / 2, 0);
  scene.add(left);
  const right = new THREE.Mesh(new THREE.BoxGeometry(bbT, bbH, ROOM.D), baseMat);
  right.position.set(halfW - bbT / 2, bbH / 2, 0);
  scene.add(right);

  // Crown molding — gold-ish to match the neon-glam vibe
  const cback  = new THREE.Mesh(new THREE.BoxGeometry(ROOM.W, crH, bbT * 1.5), crownMat);
  cback.position.set(0, ROOM.H - crH / 2, -halfD + bbT * 0.75);
  scene.add(cback);
  const cfront = new THREE.Mesh(new THREE.BoxGeometry(ROOM.W, crH, bbT * 1.5), crownMat);
  cfront.position.set(0, ROOM.H - crH / 2, halfD - bbT * 0.75);
  scene.add(cfront);
  const cleft  = new THREE.Mesh(new THREE.BoxGeometry(bbT * 1.5, crH, ROOM.D), crownMat);
  cleft.position.set(-halfW + bbT * 0.75, ROOM.H - crH / 2, 0);
  scene.add(cleft);
  const cright = new THREE.Mesh(new THREE.BoxGeometry(bbT * 1.5, crH, ROOM.D), crownMat);
  cright.position.set(halfW - bbT * 0.75, ROOM.H - crH / 2, 0);
  scene.add(cright);
}

// ---------- Entry area on the front wall ----------
// Wood door frame (around the existing dark door panel), a small welcome mat
// at the threshold, a 21+ ID decal beside the door, and two flanking planters.
function buildEntryArea(scene) {
  const frameMat = new THREE.MeshLambertMaterial({ color: 0x2c170a });
  const matMat = new THREE.MeshLambertMaterial({ color: 0x222033 });
  const potMat = new THREE.MeshLambertMaterial({ color: 0x884422 });
  const leafMat = new THREE.MeshLambertMaterial({ color: 0x4a8a3a });
  // Glass panes — translucent with a faint cool tint so the dark backdrop
  // (the "outside" plane in buildShell) reads as if seen through real glass.
  const glassMat = new THREE.MeshLambertMaterial({
    color: 0xc8e8f0, transparent: true, opacity: 0.28,

  });
  // Brass-orange handles to echo the Retro Bakery brand color.
  const handleMat = new THREE.MeshLambertMaterial({
    color: 0xffac3e,
    emissive: 0xffac3e, emissiveIntensity: 0.15,
  });

  const z = ROOM.D / 2 - 0.04;
  const doorW = 3.6, doorH = 2.6;     // wider double-door
  const cx = 0;
  const fT = 0.10;
  const headerH = 0.16;
  const mullionW = 0.10;
  const paneW = (doorW - mullionW) / 2;

  // Two glass panes (one per door) sit in front of the dark backdrop plane
  // that buildShell places on the front wall behind them.
  const paneGeo = new THREE.PlaneGeometry(paneW, doorH);
  const paneL = new THREE.Mesh(paneGeo, glassMat);
  paneL.rotation.y = Math.PI;
  paneL.position.set(cx - mullionW / 2 - paneW / 2, doorH / 2, z);
  scene.add(paneL);
  const paneR = new THREE.Mesh(paneGeo, glassMat);
  paneR.rotation.y = Math.PI;
  paneR.position.set(cx + mullionW / 2 + paneW / 2, doorH / 2, z);
  scene.add(paneR);

  // Door frame — header, two side jambs, central mullion separating the panes
  const header = new THREE.Mesh(new THREE.BoxGeometry(doorW + 0.4, headerH, 0.05), frameMat);
  header.position.set(cx, doorH + headerH / 2 + 0.02, z);
  header.rotation.y = Math.PI;
  scene.add(header);
  const jambL = new THREE.Mesh(new THREE.BoxGeometry(fT, doorH + headerH, 0.05), frameMat);
  jambL.position.set(cx - doorW / 2 - fT / 2, (doorH + headerH) / 2, z);
  jambL.rotation.y = Math.PI;
  scene.add(jambL);
  const jambR = new THREE.Mesh(new THREE.BoxGeometry(fT, doorH + headerH, 0.05), frameMat);
  jambR.position.set(cx + doorW / 2 + fT / 2, (doorH + headerH) / 2, z);
  jambR.rotation.y = Math.PI;
  scene.add(jambR);
  const mullion = new THREE.Mesh(new THREE.BoxGeometry(mullionW, doorH, 0.06), frameMat);
  mullion.position.set(cx, doorH / 2, z - 0.005);
  mullion.rotation.y = Math.PI;
  scene.add(mullion);

  // Brass door handles — vertical bars on the inside edges of each pane
  const handleGeo = new THREE.CylinderGeometry(0.022, 0.022, 0.42, 12);
  const handleL = new THREE.Mesh(handleGeo, handleMat);
  handleL.position.set(cx - mullionW / 2 - 0.10, doorH / 2 - 0.10, z - 0.025);
  scene.add(handleL);
  const handleR = new THREE.Mesh(handleGeo, handleMat);
  handleR.position.set(cx + mullionW / 2 + 0.10, doorH / 2 - 0.10, z - 0.025);
  scene.add(handleR);

  // Welcome mat just in front of the door
  const mat = new THREE.Mesh(new THREE.PlaneGeometry(doorW * 0.6, 0.7), matMat);
  mat.rotation.x = -Math.PI / 2;
  mat.position.set(cx, 0.005, ROOM.D / 2 - 0.5);
  scene.add(mat);

  // (21+ VALID ID decal moved to the cashier counter — see buildCounter.)
  // (Floor planters replaced by the giant flanking lava lamps in buildLavaLamps.)
}

// ---------- Themed display cases (one per zone) ----------
function buildZoneCases(scene) {
  for (const [name, z] of Object.entries(ZONES)) {
    buildSingleCase(scene, name, z);
  }
}

function buildSingleCase(scene, name, zone) {
  const [cx, cy, cz] = zone.center;
  const len = zone.length;
  const depth = 0.95;
  const baseHeight = 0.45;
  const caseHeight = 0.35 + zone.shelves * 0.50;       // grows with shelf count
  const totalHeight = baseHeight + caseHeight;
  const onZAxis = zone.axis === 'z';
  // Sign of "into-the-wall" direction. -1 means the back of the case is at
  // negative-X (or negative-Z); +1 means positive.
  const inwardSign =
    zone.faceDir === 'right' ? -1 :
    zone.faceDir === 'left'  ?  1 :
    zone.faceDir === 'front' ? -1 :  1;

  // Glass: previously 0xb8d4dc / opacity 0.18 — that put a noticeable
  // blue-gray haze on every product behind it. Pure white at 0.08 opacity
  // gives a subtle "there's a pane here" reflection without recoloring the
  // packaging. Bumped roughness slightly so the few highlights aren't bright
  // enough to wash out content.
  const glassMat = new THREE.MeshLambertMaterial({
    color: 0xffffff, transparent: true, opacity: 0.08,

  });
  const baseMat = new THREE.MeshLambertMaterial({ color: 0x2c170a });
  const trimMat = new THREE.MeshLambertMaterial({ color: zone.trim });
  const accentMat = new THREE.MeshBasicMaterial({ color: zone.accent, toneMapped: false });

  const wDim = onZAxis ? depth : len;
  const dDim = onZAxis ? len : depth;

  // Pedestal
  const ped = new THREE.Mesh(new THREE.BoxGeometry(wDim, baseHeight, dDim), baseMat);
  ped.position.set(cx, baseHeight / 2, cz);
  scene.add(ped);

  // Glass
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(wDim - 0.05, caseHeight, dDim - 0.05),
    glassMat,
  );
  glass.position.set(cx, baseHeight + caseHeight / 2, cz);
  scene.add(glass);

  // Top trim (zone-colored)
  const trim = new THREE.Mesh(
    new THREE.BoxGeometry(wDim + 0.06, 0.08, dDim + 0.06),
    trimMat,
  );
  trim.position.set(cx, totalHeight + 0.04, cz);
  scene.add(trim);

  // Glowing accent strip along the front edge of the trim (zone color)
  // Thin emissive plane just below the top trim, on the case's "open" face.
  const accentLen = onZAxis ? dDim : wDim;
  const accentGeo = new THREE.PlaneGeometry(accentLen + 0.05, 0.04);
  const accent = new THREE.Mesh(accentGeo, accentMat);
  accent.position.set(cx, totalHeight - 0.02, cz);
  if (onZAxis) {
    accent.rotation.y = Math.PI / 2;
    accent.position.x += inwardSign * (depth / 2 + 0.005);
  } else {
    accent.position.z += inwardSign * (depth / 2 + 0.005);
  }
  scene.add(accent);

  // ---- Cream interior back wall ----
  // Bright cream panel just inside the back glass, so products silhouette
  // cleanly against it instead of fading into the wood paneling. This is the
  // "white display backdrop" effect of real retail cases.
  const backdropMat = new THREE.MeshLambertMaterial({
    color: 0xfaf3e2,                  // warm cream, slightly off-white

    emissive: 0xfaf3e2,
    emissiveIntensity: 0.18,           // gentle self-glow so it stays bright in shadow
    // Push depth back so LED zone signs (DRINK BAR, CHOCOLATE COVE,
    // TINCTURES, GUMMY GARDEN) and other decorations attached to
    // these display-case backdrops always win the depth test on mediump
    // mobile precision.
    polygonOffset: true, polygonOffsetFactor: 4, polygonOffsetUnits: 32,
  });
  const backdropLen = onZAxis ? dDim - 0.06 : wDim - 0.06;
  const backdrop = new THREE.Mesh(
    new THREE.PlaneGeometry(backdropLen, caseHeight - 0.06),
    backdropMat,
  );
  // Position at the back interior surface, facing toward the room.
  // For onZAxis cases, the plane normal points along ±X (depending on faceDir);
  // for axis='x' cases, normal points along ±Z.
  if (onZAxis) {
    backdrop.position.set(cx + inwardSign * (depth / 2 - 0.03), baseHeight + caseHeight / 2, cz);
    // Default plane normal is +Z. Rotate so it faces -inwardSign * X (toward the room).
    backdrop.rotation.y = inwardSign === -1 ? Math.PI / 2 : -Math.PI / 2;
  } else {
    backdrop.position.set(cx, baseHeight + caseHeight / 2, cz + inwardSign * (depth / 2 - 0.03));
    backdrop.rotation.y = inwardSign === -1 ? 0 : Math.PI;
  }
  scene.add(backdrop);

  // ---- Visible shelf bars (one per row of products) ----
  // Sits just below where products' bottoms land. Spans the case length.
  const shelfMat = new THREE.MeshLambertMaterial({ color: 0x4a2811 });
  const SHELF_VSPACING = 0.50;                          // mirrors products.js
  const shelfThickness = 0.04;
  for (let s = 0; s < zone.shelves; s++) {
    const shelfCenterY = (cy - 0.05 + s * SHELF_VSPACING) - 0.17;  // 17cm below product center
    const sw = onZAxis ? depth - 0.08 : len - 0.20;
    const sd = onZAxis ? len - 0.20 : depth - 0.08;
    const shelf = new THREE.Mesh(
      new THREE.BoxGeometry(sw, shelfThickness, sd),
      shelfMat,
    );
    shelf.position.set(cx, shelfCenterY, cz);
    scene.add(shelf);
  }
}

// ---------- Central counter at back wall ----------
function buildCounter(scene) {
  const formica = makeFormicaTexture();
  formica.repeat.set(4, 1);
  const counterMat = new THREE.MeshLambertMaterial({
    map: formica,
    // Push back so the CHECKOUT sign + 21+ decal + ipad screen always win
    // the depth test. Same rationale as the room shell — mediump precision
    // on mobile can't distinguish coplanar/near-coplanar surfaces without
    // help.
    polygonOffset: true, polygonOffsetFactor: 4, polygonOffsetUnits: 32,
  });
  // Counter BODY (not the top) — also gets polygonOffset because the
  // CHECKOUT sign and 21+ decal are on the body's front face, not the top.
  const baseMat = new THREE.MeshLambertMaterial({
    color: 0x3a1f10,
    polygonOffset: true, polygonOffsetFactor: 4, polygonOffsetUnits: 32,
  });

  // Counter is now an "island" — pulled forward off the back wall so a cashier
  // can stand behind it. Player movement clamps in controls.js prevent walk-through.
  // Shifted right (cx=1.5) so the back-room doorway and tincture case have room
  // on the back-wall left half. Spans x[-0.6, 3.6] with the back-wall poster
  // (x≈6.5) sitting comfortably to the right.
  const cx = 1.5, cz = -ROOM.D / 2 + 1.5;     // 1.5m off the back wall
  const cw = 4.2, ch = 1.0, cd = 0.9;

  const base = new THREE.Mesh(new THREE.BoxGeometry(cw, ch, cd), baseMat);
  base.position.set(cx, ch / 2, cz);
  scene.add(base);

  const top = new THREE.Mesh(new THREE.BoxGeometry(cw + 0.1, 0.06, cd + 0.1), counterMat);
  top.position.set(cx, ch + 0.03, cz);
  scene.add(top);

  // iPad-style cash register: small white base, slim stand, black tablet with a
  // glowing screen on the cashier-facing side. Replaces the old beige box.
  const ipadBaseMat = new THREE.MeshLambertMaterial({ color: 0xeaeaea });
  const ipadBodyMat = new THREE.MeshLambertMaterial({ color: 0x141414 });
  const ipadScreenMat = new THREE.MeshBasicMaterial({ color: 0x4a90e2, toneMapped: false });

  const ipadX = cx - 1.2;
  const ipadZ = cz;
  const ctop  = ch + 0.06;          // counter-top y

  const ipadBase = new THREE.Mesh(
    new THREE.BoxGeometry(0.30, 0.025, 0.22),
    ipadBaseMat,
  );
  ipadBase.position.set(ipadX, ctop + 0.013, ipadZ);
  scene.add(ipadBase);

  const ipadStand = new THREE.Mesh(
    new THREE.CylinderGeometry(0.022, 0.028, 0.16, 14),
    ipadBaseMat,
  );
  ipadStand.position.set(ipadX, ctop + 0.025 + 0.08, ipadZ + 0.02);
  scene.add(ipadStand);

  const tabletY = ctop + 0.025 + 0.16 + 0.10;
  const tablet = new THREE.Mesh(
    new THREE.BoxGeometry(0.27, 0.20, 0.014),
    ipadBodyMat,
  );
  tablet.position.set(ipadX, tabletY, ipadZ + 0.04);
  scene.add(tablet);

  // Glowing UI screen on the cashier-facing side of the tablet
  const ipadScreen = new THREE.Mesh(
    new THREE.PlaneGeometry(0.245, 0.175),
    ipadScreenMat,
  );
  ipadScreen.position.set(ipadX, tabletY, ipadZ + 0.04 - 0.0085);
  ipadScreen.rotation.y = Math.PI;
  scene.add(ipadScreen);

  // Small black card reader on the customer-facing side, beside the iPad
  const readerMat = new THREE.MeshLambertMaterial({ color: 0x1a1a1a });
  const reader = new THREE.Mesh(
    new THREE.BoxGeometry(0.07, 0.11, 0.05),
    readerMat,
  );
  reader.position.set(ipadX + 0.22, ctop + 0.06, ipadZ + 0.02);
  scene.add(reader);

  // Glowing "CHECKOUT" sign on the front face of the counter, facing customers.
  // Sized big enough to read as a primary signage element across the room.
  // Pulled physically forward 0.005 → 0.05m AND given negative polygonOffset
  // so it can never z-fight the counter face on mediump mobile.
  const checkoutTex = makeZoneSignTexture('CHECKOUT', '#ffac3e');
  const checkout = new THREE.Mesh(
    new THREE.PlaneGeometry(3.0, 0.60),
    new THREE.MeshBasicMaterial({
      map: checkoutTex, transparent: true, toneMapped: false,
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
    }),
  );
  checkout.position.set(cx, ch / 2, cz + cd / 2 + 0.05);
  scene.add(checkout);

  // 21+ VALID ID decal on the back wall right next to where the cashier stands.
  // Same fix as CHECKOUT: bump physical offset 0.04 → 0.10m and negative
  // polygonOffset for guaranteed depth-test win.
  const decalTex = makeAgeDecalTexture();
  const decal = new THREE.Mesh(
    new THREE.PlaneGeometry(0.55, 0.55),
    new THREE.MeshBasicMaterial({
      map: decalTex, transparent: true, toneMapped: false,
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
    }),
  );
  decal.position.set(cx + 1.7, 1.55, -ROOM.D / 2 + 0.10);
  scene.add(decal);

  // Cashier stands well behind the counter — torso radius 0.20 + the counter
  // back face at cz - 0.45 means anything closer than cz - 0.65 visibly clips
  // the counter front. Push to cz - 1.0 for safe clearance from any angle.
  buildCashier(scene, cx + 0.5, cz - 1.0);
}

// ---------- Cashier (low-poly figure behind the counter) ----------
function buildCashier(scene, x, z) {
  // 70s-styled cashier: skin-tone head with brown hair, bright pink shirt,
  // purple flares. Built from primitives, all standard materials with a touch
  // of emissive so they read clearly in the room lighting.
  const skinMat  = new THREE.MeshLambertMaterial({ color: 0xe8b88a });
  const hairMat  = new THREE.MeshLambertMaterial({ color: 0x3a1a08 });
  const shirtMat = new THREE.MeshLambertMaterial({
    color: 0xff5fa3, emissive: 0x441026, emissiveIntensity: 0.35,
  });
  const pantsMat = new THREE.MeshLambertMaterial({
    color: 0x6a3aa0, emissive: 0x1a0a30, emissiveIntensity: 0.25,
  });
  const shoeMat  = new THREE.MeshLambertMaterial({ color: 0x1a0a05 });

  // Pants — two cylinder legs side by side
  const legGeo = new THREE.CylinderGeometry(0.10, 0.13, 0.75, 12);
  const legL = new THREE.Mesh(legGeo, pantsMat);
  legL.position.set(x - 0.10, 0.375, z);
  scene.add(legL);
  const legR = new THREE.Mesh(legGeo, pantsMat);
  legR.position.set(x + 0.10, 0.375, z);
  scene.add(legR);

  // Shoes
  const shoeGeo = new THREE.BoxGeometry(0.16, 0.06, 0.22);
  const shoeL = new THREE.Mesh(shoeGeo, shoeMat);
  shoeL.position.set(x - 0.10, 0.03, z + 0.04);
  scene.add(shoeL);
  const shoeR = new THREE.Mesh(shoeGeo, shoeMat);
  shoeR.position.set(x + 0.10, 0.03, z + 0.04);
  scene.add(shoeR);

  // Torso — slightly tapered cylinder
  const torsoGeo = new THREE.CylinderGeometry(0.20, 0.22, 0.55, 14);
  const torso = new THREE.Mesh(torsoGeo, shirtMat);
  torso.position.set(x, 1.03, z);
  scene.add(torso);

  // Arms — cylinders down the sides
  const armGeo = new THREE.CylinderGeometry(0.06, 0.07, 0.50, 10);
  const armL = new THREE.Mesh(armGeo, shirtMat);
  armL.position.set(x - 0.26, 1.05, z);
  scene.add(armL);
  const armR = new THREE.Mesh(armGeo, shirtMat);
  armR.position.set(x + 0.26, 1.05, z);
  scene.add(armR);

  // Neck
  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 0.08, 10), skinMat);
  neck.position.set(x, 1.34, z);
  scene.add(neck);

  // Head
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.135, 18, 14), skinMat);
  head.position.set(x, 1.49, z);
  scene.add(head);

  // Hair — a small cap covering only the top of the head, biased slightly
  // backward so the face stays uncovered. (Was a full 112° dome that engulfed
  // the face → "half-black face" appearance.) thetaLength PI/2.4 ≈ 75°.
  const hairGeo = new THREE.SphereGeometry(0.146, 18, 12, 0, Math.PI * 2, 0, Math.PI / 2.4);
  const hair = new THREE.Mesh(hairGeo, hairMat);
  hair.position.set(x, 1.50, z - 0.01);
  scene.add(hair);

  // Simple eyes — two tiny dark spheres on the front of the head (+Z)
  const eyeGeo = new THREE.SphereGeometry(0.018, 8, 8);
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0x1a0a05 });
  const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
  eyeL.position.set(x - 0.045, 1.51, z + 0.125);
  scene.add(eyeL);
  const eyeR = new THREE.Mesh(eyeGeo, eyeMat);
  eyeR.position.set(x + 0.045, 1.51, z + 0.125);
  scene.add(eyeR);
}

// ---------- Retro Bakery logo sign on inside of front wall ----------
function buildNeonSign(scene) {
  // Same Mali-G715 mitigation as product textures: load via
  // fetch + createImageBitmap (avoids the broken HTMLImageElement →
  // texImage2D pathway that renders black on this GPU), resize to
  // power-of-2 dimensions, and disable mipmap generation.
  const SIGN_W = 1.9, SIGN_H = SIGN_W / 1.472;       // ≈ 1.9 × 1.29
  const SIGN_Y = 3.31;

  const mat = new THREE.MeshBasicMaterial({
    transparent: true,
    toneMapped: false,
    // Pull-forward decal pattern so the sign always wins the depth test
    // against the front wall on mediump mobile.
    polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
  });
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(SIGN_W, SIGN_H), mat);
  sign.rotation.y = Math.PI;
  // Pushed from 0.04m to 0.10m for the same depth-precision reasons as
  // the other wall-attached signs.
  sign.position.set(0, SIGN_Y, ROOM.D / 2 - 0.10);
  scene.add(sign);

  // Logo aspect 1872:1272 ≈ 1.472. Resize to 1024×704 (1024 = POT, 704
  // ≈ 1024/1.472 — close enough that `linear` filtering smooths any
  // sub-pixel mismatch). 1024 is plenty of detail for a 1.9m-wide plane.
  fetch('/retro-bakery-logo.png', { mode: 'cors', credentials: 'omit' })
    .then(r => { if (!r.ok) throw new Error('http ' + r.status); return r.blob(); })
    .then(blob => createImageBitmap(blob, {
      imageOrientation: 'flipY',
      resizeWidth: 1024,
      resizeHeight: 704,
      resizeQuality: 'high',
    }))
    .then(bitmap => {
      const tex = new THREE.CanvasTexture(bitmap);
      tex.flipY = false;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.generateMipmaps = false;
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.wrapS = THREE.ClampToEdgeWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.anisotropy = 1;
      tex.needsUpdate = true;
      mat.map = tex;
      mat.needsUpdate = true;
    })
    .catch(err => {
      console.warn('[scene] retro bakery logo load failed:', err.message || err);
    });
}

// ---------- Beaded curtain on back wall (left of counter) ----------
function buildBeadedCurtain(scene) {
  const STRANDS = 28;
  const PER_STRAND = 30;
  const TOTAL = STRANDS * PER_STRAND;
  const beadGeo = new THREE.SphereGeometry(0.04, 6, 4);
  const beadMat = new THREE.MeshLambertMaterial({
    color: 0xffffff, vertexColors: true,
  });

  const inst = new THREE.InstancedMesh(beadGeo, beadMat, TOTAL);
  const palette = ['#ff5fa3', '#ffac3e', '#3fe0d4', '#a45ff0', '#ffe45e'].map(c => new THREE.Color(c));
  const colors = new Float32Array(TOTAL * 3);
  const m = new THREE.Matrix4();

  // Doorway centered at (-2.5, 1.4, -6.95) — between Tincture (right edge -4.0)
  // and the counter (left edge -0.6 at cx=1.5).
  const xC = -2.5;
  const xStart = xC - 1.0, xEnd = xC + 1.0;
  const yTop = 2.7;
  const yStep = 0.085;
  const zPos = -ROOM.D / 2 + 0.08;

  let i = 0;
  for (let s = 0; s < STRANDS; s++) {
    const x = xStart + (s / (STRANDS - 1)) * (xEnd - xStart);
    const strandCol = palette[s % palette.length];
    for (let b = 0; b < PER_STRAND; b++) {
      const y = yTop - b * yStep;
      m.makeTranslation(x, y, zPos);
      inst.setMatrixAt(i, m);
      const c = (b % 3 === 0) ? palette[(s + 1) % palette.length] : strandCol;
      colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
      i++;
    }
  }
  inst.instanceMatrix.needsUpdate = true;
  inst.instanceColor = new THREE.InstancedBufferAttribute(colors, 3);
  scene.add(inst);

  // Dark doorway behind the curtain. Pushed to 0.10m (was 0.04m) and given
  // negative polygonOffset so it always wins the depth test against the
  // back wall on mediump-precision mobile.
  const doorway = new THREE.Mesh(
    new THREE.PlaneGeometry(2.2, 2.6),
    new THREE.MeshBasicMaterial({
      color: 0x07050a,
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
    }),
  );
  doorway.position.set(xC, 1.35, -ROOM.D / 2 + 0.10);
  scene.add(doorway);

  // Doorway trim — also pulled forward 0.04m → 0.10m. Negative polygonOffset
  // ensures the trim wins against both the wall and the doorway plane.
  const trimMat = new THREE.MeshLambertMaterial({
    color: 0x2c170a,
    polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
  });
  const trimT = 0.06;
  const top = new THREE.Mesh(new THREE.BoxGeometry(2.4, trimT, 0.05), trimMat);
  top.position.set(xC, 2.66, -ROOM.D / 2 + 0.10);
  scene.add(top);
  const left = new THREE.Mesh(new THREE.BoxGeometry(trimT, 2.6, 0.05), trimMat);
  left.position.set(xC - 1.1, 1.35, -ROOM.D / 2 + 0.10);
  scene.add(left);
  const right = new THREE.Mesh(new THREE.BoxGeometry(trimT, 2.6, 0.05), trimMat);
  right.position.set(xC + 1.1, 1.35, -ROOM.D / 2 + 0.10);
  scene.add(right);
}

// ---------- Posters on walls ----------
// Each poster is unique now — varied design (sunburst / concentric / spiral /
// mushroom) and a different psychedelic phrase. Previous version reused
// the same "PEACE & LOVE" texture on every wall which read as tacky/repetitive.
// Posters live in the band BETWEEN the case top trim (≈y=2.40) and the crown
// molding (y=3.92).
function buildPosters(scene) {
  // Posters now flank the entrance on the FRONT wall (at eye level), instead
  // of sitting up above the side-wall display cases. Reasons:
  //   - Side-wall posters were too high to read at a glance — eye-line view
  //     was always tilted way up just to see them, and the title (which lives
  //     at the top of each poster — see makePosterTexture) was the part most
  //     foreshortened when close.
  //   - The front wall on either side of the door was empty space.
  // Each entry: [x, y, z, rotY, w, h, design, paletteIdx, title]
  // Posters offset 0.10m from the walls (was 0.04m / 0.02m). Mediump
  // precision on mobile depth buffers can't distinguish smaller offsets
  // from the wall, causing flicker as the camera moves. 10cm reads as
  // "decoratively floating slightly off the wall" without looking weird.
  const placements = [
    // Left of front door — eye-level. Pulled outward to leave room for the
    // giant floor lava lamps that flank the entrance.
    [-4.6, 1.55, ROOM.D / 2 - 0.10, Math.PI, 1.1, 1.5, 'sunburst', 0, 'GROOVY'],
    [-7.4, 1.55, ROOM.D / 2 - 0.10, Math.PI, 1.1, 1.5, 'mushroom', 2, 'FAR OUT'],
    // Right of front door
    [ 4.6, 1.55, ROOM.D / 2 - 0.10, Math.PI, 1.1, 1.5, 'circles',  1, 'MELLOW'],
    [ 7.4, 1.55, ROOM.D / 2 - 0.10, Math.PI, 1.1, 1.5, 'spiral',   3, 'KEEP ON'],
    // Back wall — landscape banner above the beaded curtain doorway (kept)
    [-2.5, 3.30, -ROOM.D / 2 + 0.10, 0, 1.20, 0.55, 'banner', 0, 'BACK ROOM'],
    // Back wall — large portrait Retro Bakery logo filling the wall slot
    // where the Vape Lounge case used to sit (right of the back-room doorway).
    // 1.6×2.0m so the wall doesn't read as bare. Faces +Z (rotY=0) to be
    // visible from the shop floor. Centered horizontally at x=6.5; bottom at
    // y≈0.6, top at y≈2.6, well under the crown molding. design='logo' opts
    // out of the procedural sunburst/circles/spiral path and loads
    // /retro-bakery-logo.png as a transparent overlay on the wall.
    [ 6.5, 1.60, -ROOM.D / 2 + 0.10, 0, 1.60, 2.00, 'logo', 0, ''],
  ];
  // Lazy texture loader shared across logo posters. Loading the PNG once and
  // reusing the texture keeps draw memory low and avoids a flicker if the
  // same logo appears in multiple places later.
  const _texLoader = new THREE.TextureLoader();
  let _logoTex = null;
  const getLogoTexture = () => {
    if (_logoTex) return _logoTex;
    _logoTex = _texLoader.load('/retro-bakery-logo.png');
    _logoTex.colorSpace = THREE.SRGBColorSpace;
    _logoTex.anisotropy = 4;
    return _logoTex;
  };

  for (const [x, y, z, ry, w, h, design, palette, title] of placements) {
    let material;
    if (design === 'logo') {
      // Logo PNG has a transparent background — MeshBasicMaterial with
      // transparent:true keeps the wall behind the non-logo pixels. depthWrite
      // off so the transparent edges don't carve a depth-buffer hole that
      // other transparent things (lava lamps, signs) sort wrong against.
      const tex = getLogoTexture();
      material = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
        polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
      });
    } else {
      const tex = makePosterTexture({ design, palette, title });
      material = new THREE.MeshLambertMaterial({
        map: tex,
        emissive: 0xffffff,
        emissiveMap: tex,
        emissiveIntensity: 0.55,
        // Pull-forward polygon offset so posters always win the depth test
        // against the wall on mediump mobile.
        polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
      });
    }
    const poster = new THREE.Mesh(new THREE.PlaneGeometry(w, h), material);
    poster.position.set(x, y, z);
    poster.rotation.y = ry;
    scene.add(poster);
  }
}

// ---------- Vinyl shelf — runs the length of the back wall behind the cashier ----------
function buildVinylShelf(scene) {
  const shelfMat = new THREE.MeshLambertMaterial({ color: 0x3a1f10 });

  // Centered behind the shifted cashier counter (counter cx=1.5). Width sized
  // to fit between the back-room doorway right edge (-1.5) and the back-wall
  // poster (x≈6.5) with margins.
  const CENTER_X = 1.5;
  const COUNT = 10;
  const RADIUS = 0.24;
  const SPACING = 0.50;          // center-to-center, small gap between records
  const Y_CENTER = 2.10;
  const Z = -ROOM.D / 2 + 0.20;

  // Shelf — same length as the row of records plus end-caps.
  const shelfWidth = (COUNT - 1) * SPACING + RADIUS * 2 + 0.20;
  const shelf = new THREE.Mesh(new THREE.BoxGeometry(shelfWidth, 0.04, 0.32), shelfMat);
  shelf.position.set(CENTER_X, Y_CENTER - RADIUS - 0.04, Z);
  scene.add(shelf);

  const palette = ['#ff4fa3', '#ffac3e', '#3fe0d4', '#a45ff0', '#ffe45e', '#ff5e2e'];
  const recordGeo = new THREE.CylinderGeometry(RADIUS, RADIUS, 0.006, 32);
  const startX = CENTER_X - ((COUNT - 1) * SPACING) / 2;
  for (let i = 0; i < COUNT; i++) {
    const labelTex = makeVinylLabel(palette[i % palette.length]);
    const mat = new THREE.MeshLambertMaterial({ map: labelTex });
    const rec = new THREE.Mesh(recordGeo, mat);
    rec.rotation.x = Math.PI / 2;
    rec.position.set(startX + i * SPACING, Y_CENTER, Z);
    scene.add(rec);
  }
}

// ---------- Lava lamps (animated) ----------
// Each lamp is base + translucent glass body + cap, plus three "wax" blobs
// inside the glass that bob up and down on their own phase. The blobs are
// rendered with MeshBasicMaterial + toneMapped:false so they read clearly
// through the glass regardless of room lighting. Per-frame motion is driven
// by updateAnimations() at the top of this file.
function buildLavaLamps(scene) {
  // [x, y, z, glassColor, scale=1]. Uniform scale multiplies every dimension
  // (radii, heights, blob amplitude) so the lamp keeps its natural proportions
  // — used for the big entryway lamps that flank the front door.
  const lamps = [
    [-9.4, 2.4, -5.0, 0xff5e2e],
    [ 9.4, 2.4,  5.0, 0xff4fa3],
    [-9.4, 2.4,  6.0, 0xffac3e],
    [ 9.4, 2.4, -5.5, 0x3fe0d4],
    [-0.2, 1.05, -ROOM.D / 2 + 1.5, 0xa45ff0],            // on counter
    [-3.5, 0,    ROOM.D / 2 - 0.9, 0xff4fa3, 6.0],        // giant, left of door
    [ 3.5, 0,    ROOM.D / 2 - 0.9, 0xffac3e, 6.0],        // giant, right of door
  ];
  const baseMat = new THREE.MeshLambertMaterial({ color: 0x33180a });
  const tmpColor = new THREE.Color();

  lamps.forEach(([x, y, z, color, scale = 1], lampIdx) => {
    // Base
    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09 * scale, 0.11 * scale, 0.08 * scale, 16),
      baseMat,
    );
    base.position.set(x, y + 0.04 * scale, z);
    scene.add(base);

    // Glass body — quite transparent so the wax inside reads clearly
    const glassMat = new THREE.MeshBasicMaterial({
      color, toneMapped: false, transparent: true, opacity: 0.30,
    });
    const glass = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06 * scale, 0.085 * scale, 0.32 * scale, 16),
      glassMat,
    );
    glass.position.set(x, y + 0.24 * scale, z);
    scene.add(glass);

    // Cap — shorter cone so the top reads as a stubby lava-lamp nub instead
    // of a tall spike. Sits flush on top of the glass.
    const capH = 0.035;
    const cap = new THREE.Mesh(
      new THREE.ConeGeometry(0.07 * scale, capH * scale, 16),
      baseMat,
    );
    cap.position.set(x, y + (0.40 + capH / 2) * scale, z);
    scene.add(cap);

    // Wax blobs — opaque + brightly tinted so they pop through the glass.
    tmpColor.setHex(color).lerp(new THREE.Color(0xffffff), 0.7);
    const blobMat = new THREE.MeshBasicMaterial({
      color: tmpColor.getHex(),
      toneMapped: false,
    });
    const blobGeo = new THREE.SphereGeometry(0.045 * scale, 8, 6);

    for (let b = 0; b < 3; b++) {
      const blob = new THREE.Mesh(blobGeo, blobMat);
      const offsetAngle = (b / 3) * Math.PI * 2;
      const offsetR = 0.010 * scale;
      // baseY spread (0.13 → 0.27) keeps the top blob's bob amplitude well
      // inside the glass — the old (0.16 → 0.32) reached the cap base and the
      // top of the blob protruded out through the conical cap surface near
      // its narrow tip.
      const blobBaseY = y + (0.13 + b * 0.07) * scale;
      blob.position.set(
        x + Math.cos(offsetAngle) * offsetR,
        blobBaseY,
        z + Math.sin(offsetAngle) * offsetR,
      );
      blob.renderOrder = 1; // render after the glass for correct alpha sorting
      scene.add(blob);

      _animatedBlobs.push({
        mesh: blob,
        baseY: blobBaseY,
        amplitude: 0.060 * scale,                 // tightened from 0.075 for safer headroom
        speed: 0.30 + b * 0.08 + lampIdx * 0.04,
        phase: lampIdx * 1.7 + b * 2.1,
        baseScale: 1,
      });
    }
  });
}

// ---------- Hanging disco balls ----------
function buildHangingPlants(scene) {
  // 3 × 3 grid evenly distributed across the ceiling (room is 20 × 14, balls
  // ~6m apart in x and 4m apart in z, leaving roughly equal margins to walls).
  const positions = [
    [-6, -4], [0, -4], [6, -4],
    [-6,  0], [0,  0], [6,  0],
    [-6,  4], [0,  4], [6,  4],
  ];
  // Thin chain dangling from the ceiling.
  const chainMat = new THREE.MeshLambertMaterial({ color: 0x888888 });
  // Mirrored ball — silvery, very metallic, with a subtle self-glow so it
  // reads as "shiny disco ball" without an environment map.
  const ballMat = new THREE.MeshLambertMaterial({
    color: 0xcfd5dc,


    emissive: 0xffffff,
    emissiveIntensity: 0.18,
  });
  // Wireframe overlay traces the sphere facets — gives the "mirror tile" grid
  // look that sells the disco ball even in flat lighting.
  const wireMat = new THREE.LineBasicMaterial({ color: 0x555a60, transparent: true, opacity: 0.55 });

  const radius = 0.22;
  const sphereGeo = new THREE.SphereGeometry(radius, 16, 12);
  const wireGeo = new THREE.WireframeGeometry(sphereGeo);

  for (const [x, z] of positions) {
    const chainLen = 0.6;
    const ballY = ROOM.H - chainLen - radius;
    const chain = new THREE.Mesh(new THREE.CylinderGeometry(0.006, 0.006, chainLen, 6), chainMat);
    chain.position.set(x, ROOM.H - chainLen / 2, z);
    scene.add(chain);

    const ball = new THREE.Mesh(sphereGeo, ballMat);
    ball.position.set(x, ballY, z);
    scene.add(ball);

    const wires = new THREE.LineSegments(wireGeo, wireMat);
    wires.position.set(x, ballY, z);
    scene.add(wires);
  }
}

// ---------- Mushroom motifs (REMOVED — kept here as a no-op stub in case any
// older code path still references it). The build pipeline no longer calls
// this. Delete entirely once we're sure nothing imports it.
// eslint-disable-next-line no-unused-vars
function buildMushrooms_DISABLED(scene) {
  const stemMat = new THREE.MeshLambertMaterial({ color: 0xf4ead0 });
  const capMat = new THREE.MeshLambertMaterial({ color: 0xc8362d });
  const spotMat = new THREE.MeshLambertMaterial({ color: 0xffffff });

  // The y values below are the EXACT top-trim heights of each case so the
  // mushrooms sit flush instead of hovering ~17cm above. 3-shelf cases:
  // baseHeight 0.45 + caseHeight 1.85 + half-trim 0.04 + half-trim 0.04 = 2.38.
  // 1-shelf case (Tincture): 0.45 + 0.85 + 0.08 = 1.38. Counter top: 1.06.
  const placements = [
    // Top of Gummy Garden (left wall, 3 shelves)
    [-9.3, 2.38,  3.0,  0.8],
    [-9.3, 2.38, -3.0,  0.7],
    [-9.3, 2.38,  5.5,  0.55],
    // Top of Chocolate Cove (right wall front, 3 shelves)
    [ 9.3, 2.38,  4.0,  0.85],
    [ 9.3, 2.38,  2.0,  0.55],
    // Top of Drink Bar (right wall back, 3 shelves)
    [ 9.3, 2.38, -3.0,  0.7],
    [ 9.3, 2.38, -5.0,  0.55],
    // Top of back-wall kiosks (1 shelf, lower trim)
    [-7.5, 1.38, -6.55, 0.55],
    [ 6.5, 1.38, -6.55, 0.55],
    // Counter top — placed clear of the cash register (x≈-1.4) and lava lamp (x≈0.5)
    [-2.1, 1.06, -ROOM.D / 2 + 0.55, 0.5],
    [ 2.2, 1.06, -ROOM.D / 2 + 0.55, 0.55],
  ];
  // Lower-poly variants: 8-segment stem cylinder, 10×4 hemisphere cap, 6×4
  // spot spheres. Mushroom silhouette is unchanged at viewing distance but
  // tri count drops ~70% per mushroom (from ~548 to ~150 tris).
  for (const [x, y, z, scale] of placements) {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 0.18, 8), stemMat);
    stem.position.set(x, y + 0.09 * scale, z);
    stem.scale.setScalar(scale);
    scene.add(stem);
    const cap = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 10, 4, 0, Math.PI * 2, 0, Math.PI / 2),
      capMat,
    );
    cap.position.set(x, y + 0.18 * scale, z);
    cap.scale.setScalar(scale);
    scene.add(cap);
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const r = 0.1 * scale;
      const spot = new THREE.Mesh(new THREE.SphereGeometry(0.028 * scale, 6, 4), spotMat);
      spot.position.set(x + Math.cos(angle) * r, y + 0.22 * scale, z + Math.sin(angle) * r);
      scene.add(spot);
    }
  }
}

// ---------- Chocolate fountain (Willy-Wonka prop #1) ----------
// Three-tier cascading cone of brown emissive chocolate, sitting on a base.
// Positioned in front of the Chocolate Cove zone.
function buildChocolateFountain(scene) {
  const x = 6.5, z = 4.5;       // in front of Chocolate Cove
  const baseMat = new THREE.MeshLambertMaterial({ color: 0x3a1f10 });
  const goldMat = new THREE.MeshLambertMaterial({ color: 0xc89a3b });
  const chocMat = new THREE.MeshLambertMaterial({
    color: 0x6a3818, emissive: 0x4a2410, emissiveIntensity: 0.4,

  });

  // Pedestal base
  const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.65, 0.35, 24), baseMat);
  ped.position.set(x, 0.175, z);
  scene.add(ped);

  // Catch basin (gold ring)
  const basin = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 0.08, 24), goldMat);
  basin.position.set(x, 0.39, z);
  scene.add(basin);

  // Three cascading tiers — cones stacked
  const tiers = [
    { r: 0.42, h: 0.25, y: 0.55 },
    { r: 0.30, h: 0.22, y: 0.85 },
    { r: 0.18, h: 0.18, y: 1.10 },
  ];
  for (const t of tiers) {
    const tier = new THREE.Mesh(new THREE.ConeGeometry(t.r, t.h, 24, 1, true), chocMat);
    tier.position.set(x, t.y, z);
    scene.add(tier);
    // Gold band at base of each tier
    const band = new THREE.Mesh(new THREE.CylinderGeometry(t.r * 0.95, t.r * 0.95, 0.025, 24), goldMat);
    band.position.set(x, t.y - t.h / 2 + 0.02, z);
    scene.add(band);
  }

  // Top spout finial
  const finial = new THREE.Mesh(new THREE.SphereGeometry(0.06, 12, 8), goldMat);
  finial.position.set(x, 1.25, z);
  scene.add(finial);

  // "Chocolate stream" — thin emissive cylinders cascading from each tier rim
  // Just a few stylized streamers, not a real simulation.
  for (const t of tiers.slice(0, 2)) {
    for (let s = 0; s < 6; s++) {
      const a = (s / 6) * Math.PI * 2;
      const sx = x + Math.cos(a) * t.r * 0.9;
      const sz = z + Math.sin(a) * t.r * 0.9;
      const stream = new THREE.Mesh(
        new THREE.CylinderGeometry(0.012, 0.012, 0.18, 6),
        chocMat,
      );
      stream.position.set(sx, t.y - t.h / 2 - 0.09, sz);
      scene.add(stream);
    }
  }
}

// ---------- Gummy mountain (Willy-Wonka prop #2) ----------
// Pile of multicolored gummy spheres on a low wood pedestal in front of the
// Gummy Garden zone. Single InstancedMesh — one draw call.
function buildGummyMountain(scene) {
  const cx = -6.5, cy = 0, cz = -3.5;
  const baseMat = new THREE.MeshLambertMaterial({ color: 0x3a1f10 });
  const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.95, 0.30, 24), baseMat);
  ped.position.set(cx, 0.15, cz);
  scene.add(ped);

  // Mountain of gummies — 6×4 sphere keeps the silhouette readable while
  // keeping the prop's tri count low (~36 tris × 220 instances ≈ 8k).
  const COUNT = 220;
  const gummyGeo = new THREE.SphereGeometry(0.10, 6, 4);
  // MeshBasicMaterial with toneMapped:false renders the per-instance colors
  // at full saturation regardless of lighting — gummies pop like candy under
  // a display light. Don't set vertexColors:true — InstancedMesh.instanceColor
  // is consumed automatically by Three.js's instancing shader.
  const gummyMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    toneMapped: false,
  });
  const inst = new THREE.InstancedMesh(gummyGeo, gummyMat, COUNT);
  const colorPalette = [
    new THREE.Color('#ff4fa3'), new THREE.Color('#ffac3e'),
    new THREE.Color('#3fe0d4'), new THREE.Color('#a45ff0'),
    new THREE.Color('#ffe45e'), new THREE.Color('#ff6b6b'),
    new THREE.Color('#6fff8a'), new THREE.Color('#ff9f3a'),
  ];
  const m = new THREE.Matrix4();
  const pos = new THREE.Vector3();
  const quat = new THREE.Quaternion();
  const scl = new THREE.Vector3(1, 1, 1);
  const tmpColor = new THREE.Color();

  // Stack into a rough cone using layered rings
  let idx = 0;
  const rings = [
    { count: 60, r: 0.78, y: 0.36 },
    { count: 50, r: 0.62, y: 0.52 },
    { count: 40, r: 0.46, y: 0.66 },
    { count: 30, r: 0.32, y: 0.78 },
    { count: 22, r: 0.20, y: 0.88 },
    { count: 12, r: 0.10, y: 0.96 },
    { count: 6,  r: 0.04, y: 1.02 },
  ];
  for (const ring of rings) {
    for (let k = 0; k < ring.count && idx < COUNT; k++) {
      const a = (k / ring.count) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
      const rJitter = ring.r * (0.85 + Math.random() * 0.30);
      pos.set(cx + Math.cos(a) * rJitter, cy + ring.y + (Math.random() - 0.5) * 0.05, cz + Math.sin(a) * rJitter);
      m.compose(pos, quat, scl);
      inst.setMatrixAt(idx, m);
      tmpColor.copy(colorPalette[(idx + Math.floor(Math.random() * 3)) % colorPalette.length]);
      inst.setColorAt(idx, tmpColor);
      idx++;
    }
  }
  inst.count = idx;
  inst.instanceMatrix.needsUpdate = true;
  if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
  scene.add(inst);
}

// ---------- Glowing zone signs above each display ----------
// Floating neon-style label per zone — themed to the zone color. Pure emissive.
function buildZoneSigns(scene) {
  const labels = {
    Tincture:  { text: 'TINCTURES',      color: '#ffffff' },
    Gummy:     { text: 'GUMMY GARDEN',   color: '#ff4fa3' },
    Chocolate: { text: 'CHOCOLATE COVE', color: '#ffac3e' },
    Drink:     { text: 'DRINK BAR',      color: '#3fe0d4' },
  };

  for (const [name, zone] of Object.entries(ZONES)) {
    const def = labels[name];
    if (!def) continue;
    const tex = makeZoneSignTexture(def.text, def.color);
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, toneMapped: false,
      // Pull-forward decal pattern: negative polygonOffset makes this sign
      // always pass the depth test against the wall AND the case backdrop
      // it sits in front of. Belt-and-suspenders with the positive offset
      // on those background materials.
      polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -32,
    });

    const onZAxis = zone.axis === 'z';
    const sw = onZAxis ? Math.min(zone.length, 4.5) : Math.min(zone.length, 4.0);
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(sw, 0.45), mat);

    const [cx, , cz] = zone.center;
    const signY = 3.4;
    plane.position.set(cx, signY, cz);

    // Pin near the wall but not flush — bumped from 0.04m to 0.10m so
    // mediump-precision mobile depth buffers can reliably distinguish the
    // sign from the wall. Combined with polygonOffset on the wall material
    // this gives every wall-attached element a guaranteed depth-test win.
    const wallOffset = 0.10;
    if (zone.faceDir === 'right') {
      plane.rotation.y = Math.PI / 2;
      plane.position.x = -ROOM.W / 2 + wallOffset;
    } else if (zone.faceDir === 'left') {
      plane.rotation.y = -Math.PI / 2;
      plane.position.x = ROOM.W / 2 - wallOffset;
    } else if (zone.faceDir === 'front') {
      plane.rotation.y = 0;
      plane.position.z = -ROOM.D / 2 + wallOffset;
    } else if (zone.faceDir === 'back') {
      plane.rotation.y = Math.PI;
      plane.position.z = ROOM.D / 2 - wallOffset;
    }
    scene.add(plane);
  }
}

/* =====================================================================
 * Procedural texture generators — pure CanvasTexture, no asset files.
 * ===================================================================== */

function makeShagCarpetTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#a04515';
  ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 7000; i++) {
    const x = Math.random() * 256;
    const y = Math.random() * 256;
    const len = 4 + Math.random() * 10;
    const angle = Math.random() * Math.PI * 2;
    const r = Math.random();
    ctx.strokeStyle = r > 0.6 ? '#c95f1c' : r > 0.3 ? '#7a3010' : r > 0.1 ? '#e07024' : '#5a200a';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
    ctx.stroke();
  }
  return finalize(c);
}

function makeWoodPanelTexture() {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 512;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#5a2f15';
  ctx.fillRect(0, 0, 256, 512);
  for (let x = 0; x < 256; x += 3) {
    ctx.fillStyle = `rgba(${30 + Math.random() * 50}, ${15 + Math.random() * 25}, ${5 + Math.random() * 12}, ${0.15 + Math.random() * 0.55})`;
    ctx.fillRect(x, 0, 1 + Math.random() * 3, 512);
  }
  ctx.strokeStyle = '#1a0a05';
  ctx.lineWidth = 2;
  for (let y = 0; y < 512; y += 90) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(256, y); ctx.stroke();
  }
  for (let i = 0; i < 6; i++) {
    const cx = Math.random() * 256, cy = Math.random() * 512;
    const grad = ctx.createRadialGradient(cx, cy, 1, cx, cy, 10 + Math.random() * 8);
    grad.addColorStop(0, '#1a0a05'); grad.addColorStop(1, 'rgba(26,10,5,0)');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(cx, cy, 14, 0, Math.PI * 2); ctx.fill();
  }
  return finalize(c);
}

function makeCeilingTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#f0e6d0'; ctx.fillRect(0, 0, 128, 128);
  for (let i = 0; i < 1500; i++) {
    ctx.fillStyle = `rgba(150,130,100,${Math.random() * 0.4})`;
    ctx.beginPath(); ctx.arc(Math.random() * 128, Math.random() * 128, 0.5 + Math.random() * 1.5, 0, Math.PI * 2); ctx.fill();
  }
  return finalize(c);
}

function makeFormicaTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#9a6b35'; ctx.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 30; i++) {
    ctx.strokeStyle = `rgba(${50 + Math.random() * 40}, ${30 + Math.random() * 25}, ${10 + Math.random() * 15}, 0.5)`;
    ctx.lineWidth = 1 + Math.random() * 2;
    ctx.beginPath();
    let y = Math.random() * 256;
    ctx.moveTo(0, y);
    for (let x = 0; x <= 256; x += 8) { y += (Math.random() - 0.5) * 4; ctx.lineTo(x, y); }
    ctx.stroke();
  }
  return finalize(c);
}

function makeNeonSignTexture() {
  const c = document.createElement('canvas');
  c.width = 1280; c.height = 256;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, c.width, c.height);
  // Use textAlign='left' + measured widths so we control the visual gap
  // between RETRO and BAKERY exactly. Letters used to overlap because both
  // strings were center-aligned with hard-coded x positions that didn't
  // account for actual rendered width at this font weight.
  ctx.font = 'bold 150px "Trebuchet MS", "Comic Sans MS", sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  const retro = 'RETRO', bakery = 'BAKERY';
  const wRetro  = ctx.measureText(retro).width;
  const wBakery = ctx.measureText(bakery).width;
  const GAP = 80;                                              // pixels between words
  const totalW = wRetro + GAP + wBakery;
  const startX = (c.width - totalW) / 2;
  // Shift draw to "centered" by passing the LEFT edge of each word
  drawGlowText(ctx, retro,  startX,                  c.height / 2, '#ff4fa3', 32);
  drawGlowText(ctx, bakery, startX + wRetro + GAP,   c.height / 2, '#3fe0d4', 32);
  return finalize(c, false);
}

function makeNeonTaglineTexture() {
  const c = document.createElement('canvas');
  c.width = 1024; c.height = 96;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.font = 'bold 60px "Trebuchet MS", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  drawGlowText(ctx, '~ A 1970s DISPENSARY ~', c.width / 2, c.height / 2, '#ffac3e', 18);
  return finalize(c, false);
}

function makeZoneSignTexture(text, color) {
  const c = document.createElement('canvas');
  c.width = 1024; c.height = 128;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, 1024, 128);
  ctx.font = 'bold 78px "Trebuchet MS", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  drawGlowText(ctx, text, 512, 64, color, 22);
  return finalize(c, false);
}

function drawGlowText(ctx, text, x, y, color, blur) {
  ctx.shadowColor = color;
  ctx.shadowBlur = blur;
  ctx.fillStyle = color;
  for (let i = 0; i < 3; i++) ctx.fillText(text, x, y);
  ctx.shadowBlur = blur / 5;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(text, x, y);
  ctx.shadowBlur = 0;
}

// `spec` = { design: 'circles' | 'sunburst' | 'spiral' | 'mushroom' | 'banner',
//            palette: 0..3, title: string }
// Each design is rendered into its own canvas; titles vary per poster so the
// shop doesn't look like a "PEACE & LOVE" warehouse.
function makePosterTexture(spec) {
  const { design = 'circles', palette: paletteIdx = 0, title = 'GROOVY' } = (spec ?? {});
  const palettes = [
    ['#ff4fa3', '#ffac3e', '#3fe0d4', '#a45ff0', '#1a0010'],
    ['#ffac3e', '#ff4fa3', '#ffe45e', '#3fe0d4', '#1a0810'],
    ['#3fe0d4', '#a45ff0', '#ff4fa3', '#ffe45e', '#001a18'],
    ['#a45ff0', '#3fe0d4', '#ffe45e', '#ff4fa3', '#0a0018'],
  ];
  const palette = palettes[paletteIdx % palettes.length];
  const ink = palette[4];

  // Banner is short + wide. Everything else is portrait.
  const isBanner = design === 'banner';
  const c = document.createElement('canvas');
  c.width  = isBanner ? 512 : 256;
  c.height = isBanner ? 234 : 358;
  const ctx = c.getContext('2d');

  switch (design) {
    case 'sunburst':  drawSunburstPoster(ctx, c, palette); break;
    case 'spiral':    drawSpiralPoster(ctx, c, palette); break;
    case 'mushroom':  drawMushroomPoster(ctx, c, palette); break;
    case 'banner':    drawBannerPoster(ctx, c, palette); break;
    case 'circles':
    default:          drawCirclesPoster(ctx, c, palette); break;
  }

  // Title plate at the TOP of the poster — sits at the highest part of the
  // wall poster, which is the easiest area to read without tilting the
  // camera way down when standing close. (Was at the bottom before; close
  // approach foreshortened it heavily.)
  if (!isBanner) {
    const plateY = 16;
    ctx.fillStyle = palette[0];
    roundRectPath(ctx, 16, plateY, c.width - 32, 50, 12); ctx.fill();
    ctx.strokeStyle = ink; ctx.lineWidth = 3;
    roundRectPath(ctx, 16, plateY, c.width - 32, 50, 12); ctx.stroke();
    ctx.fillStyle = ink;
    ctx.font = 'bold 32px "Trebuchet MS", "Comic Sans MS", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, c.width / 2, plateY + 26);
  } else {
    // Banner: huge centered title in palette[1] with ink outline
    ctx.fillStyle = ink;
    ctx.fillRect(0, c.height / 2 - 50, c.width, 100);
    ctx.strokeStyle = palette[0]; ctx.lineWidth = 6;
    ctx.strokeRect(8, c.height / 2 - 50, c.width - 16, 100);
    ctx.fillStyle = palette[1];
    ctx.font = 'bold 70px "Trebuchet MS", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, c.width / 2, c.height / 2);
  }

  return finalize(c);
}

function drawCirclesPoster(ctx, c, palette) {
  ctx.fillStyle = palette[0]; ctx.fillRect(0, 0, c.width, c.height);
  // Design center sits BELOW the top title plate (which occupies y=0..72)
  const cx = c.width / 2, cy = c.height / 2 + 30;
  for (let r = 200; r > 0; r -= 14) {
    ctx.fillStyle = palette[(r / 14 | 0) % 4];
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
  }
}

function drawSunburstPoster(ctx, c, palette) {
  ctx.fillStyle = palette[3]; ctx.fillRect(0, 0, c.width, c.height);
  const cx = c.width / 2, cy = c.height / 2 + 30;
  const rays = 16;
  for (let i = 0; i < rays; i++) {
    ctx.fillStyle = palette[i % 3];
    ctx.beginPath();
    const a1 = (i / rays) * Math.PI * 2;
    const a2 = ((i + 0.6) / rays) * Math.PI * 2;
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a1) * 220, cy + Math.sin(a1) * 220);
    ctx.lineTo(cx + Math.cos(a2) * 220, cy + Math.sin(a2) * 220);
    ctx.closePath();
    ctx.fill();
  }
  // Sun face (simple smiley)
  ctx.fillStyle = palette[1];
  ctx.beginPath(); ctx.arc(cx, cy, 50, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = palette[4];
  ctx.beginPath(); ctx.arc(cx - 16, cy - 8, 4, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(cx + 16, cy - 8, 4, 0, Math.PI * 2); ctx.fill();
  ctx.lineWidth = 4; ctx.strokeStyle = palette[4];
  ctx.beginPath(); ctx.arc(cx, cy + 5, 18, 0, Math.PI); ctx.stroke();
}

function drawSpiralPoster(ctx, c, palette) {
  ctx.fillStyle = palette[0]; ctx.fillRect(0, 0, c.width, c.height);
  const cx = c.width / 2, cy = c.height / 2 + 30;
  ctx.lineWidth = 12;
  for (let band = 0; band < 4; band++) {
    ctx.strokeStyle = palette[band];
    ctx.beginPath();
    for (let t = 0; t < Math.PI * 6; t += 0.05) {
      const r = 6 + t * 7 + band * 3;
      const a = t + band * 0.4;
      const x = cx + Math.cos(a) * r;
      const y = cy + Math.sin(a) * r;
      if (t === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
}

function drawMushroomPoster(ctx, c, palette) {
  // Big amanita over a colored field
  ctx.fillStyle = palette[2]; ctx.fillRect(0, 0, c.width, c.height);
  // Ground
  ctx.fillStyle = palette[3];
  ctx.beginPath();
  ctx.arc(c.width / 2, c.height + 40, c.width * 0.7, Math.PI, 0);
  ctx.fill();
  // Stem
  const sx = c.width / 2, sy = c.height - 110;
  ctx.fillStyle = palette[1];
  roundRectPath(ctx, sx - 26, sy, 52, 90, 14); ctx.fill();
  // Cap
  ctx.fillStyle = '#c8362d';
  ctx.beginPath();
  ctx.arc(sx, sy + 6, 76, Math.PI, 0);
  ctx.fill();
  // Spots
  ctx.fillStyle = '#ffffff';
  for (const [dx, dy, r] of [[-30, -10, 10], [-5, -25, 8], [22, -12, 10], [40, -5, 6]]) {
    ctx.beginPath();
    ctx.arc(sx + dx, sy + dy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawBannerPoster(ctx, c, palette) {
  // Diagonal stripes
  ctx.fillStyle = palette[2]; ctx.fillRect(0, 0, c.width, c.height);
  ctx.save();
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate(-0.12);
  for (let i = -10; i < 20; i++) {
    ctx.fillStyle = palette[i & 1 ? 1 : 0];
    ctx.fillRect(-c.width, -10 + i * 24, c.width * 3, 16);
  }
  ctx.restore();
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function makeAgeDecalTexture() {
  const c = document.createElement('canvas');
  c.width = c.height = 256;
  const ctx = c.getContext('2d');
  ctx.clearRect(0, 0, 256, 256);
  const accent = '#ffac3e';      // Retro Bakery brand orange
  // Filled circle background with a thin ring border — looks like a sticker
  ctx.fillStyle = '#000000';
  ctx.beginPath(); ctx.arc(128, 128, 118, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = accent;
  ctx.lineWidth = 6;
  ctx.beginPath(); ctx.arc(128, 128, 118, 0, Math.PI * 2); ctx.stroke();
  // "21+" text with neon glow — smaller font and lower center than before so
  // the digits + glow stay well inside the circle (was clipping at top).
  ctx.font = 'bold 110px "Trebuchet MS", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  drawGlowText(ctx, '21+', 128, 118, accent, 16);
  // Tiny sub-text
  ctx.font = 'bold 22px sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.shadowBlur = 0;
  ctx.fillText('VALID ID', 128, 198);
  return finalize(c, false);
}

function makeVinylLabel(centerColor) {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#0a0a0a'; ctx.fillRect(0, 0, 128, 128);
  ctx.beginPath(); ctx.arc(64, 64, 64, 0, Math.PI * 2); ctx.fillStyle = '#0a0a0a'; ctx.fill();
  ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 0.6;
  for (let r = 20; r < 60; r += 3) { ctx.beginPath(); ctx.arc(64, 64, r, 0, Math.PI * 2); ctx.stroke(); }
  ctx.fillStyle = centerColor;
  ctx.beginPath(); ctx.arc(64, 64, 22, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#000';
  ctx.beginPath(); ctx.arc(64, 64, 3, 0, Math.PI * 2); ctx.fill();
  return finalize(c);
}

function finalize(canvas, sRGB = true) {
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  if (sRGB) tex.colorSpace = THREE.SRGBColorSpace;
  // CRITICAL for mobile: ARM Mali-G715 (and similar mobile GPUs) corrupt
  // auto-generated mipmaps for CanvasTexture sources, producing pitch-black
  // lower-resolution mip levels. The GPU samples those levels for fragments
  // far from the camera — which is exactly the "everything beyond a circle
  // around me goes dark" artifact. Disabling mipmaps forces the GPU to
  // sample level 0 (the actual canvas) at every distance with bilinear
  // smoothing. Slight aliasing in the very far distance is acceptable.
  tex.generateMipmaps = false;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 1;
  tex.needsUpdate = true;
  return tex;
}
